import * as React from 'react'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { i18nDateToString } from 'modules/core/lib/dates'

import { useSelectableShiftsRows } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Header/useSelectableShiftsRows'
import type {
  Column,
  ColumnsType,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

import HeaderCell from './HeaderCell'

interface ResultRefs {
  [key: number]: HTMLDivElement | null
}

interface HeaderProps {
  columns: ColumnsType
  shiftsRows: ShiftsRow[]
}

const Header = ({ columns, shiftsRows }: HeaderProps) => {
  const [{ view }] = useSchedule()
  const { columnDaysSelected, allTableSelected } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()
  const [{ hasMore }] = usePagination()

  const { fetch } = useSelectableShiftsRows()

  const onSelect = async (index: number, day: moment.Moment) => {
    // TODO: Refactor this use a better strategy to toggle the selection of all shifts rows
    // Right now, we're fetching all shifts rows on every selection, which is not efficient.
    // Also, refactor src/modules/shift_management/hooks/reducers/selection.ts
    if (index === 0) {
      if (allTableSelected) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
      } else {
        const selectedShiftsRows = await fetch({ startAt: view.startAt, endAt: view.endAt })
        dispatchSelection({
          type: 'TOGGLE_ALL_SHIFTS_ROWS',
          shiftsRows: selectedShiftsRows,
          view,
        })
      }
    } else {
      const dayStr = i18nDateToString(day, 'backend')
      const selectedShiftsRows = hasMore
        ? await fetch({ startAt: dayStr, endAt: dayStr })
        : shiftsRows
      dispatchSelection({ type: 'TOGGLE_DAY_COLUMN', shiftsRows: selectedShiftsRows, day, view })
    }
  }

  const checked = (index: number, dayId: string) =>
    index === 0 ? allTableSelected : columnDaysSelected.includes(dayId)

  const canManageShifts = shiftsRows.some((row) => {
    return row.canManage
  })

  const getFirstShiftIndex = (): number | null => {
    const startingHours = shiftsRows.reduce((result: number[], row) => {
      const hour = row.startingHour(view.firstDay)
      if (hour != null) result.push(hour) // != null to consider shifts starting at 0
      return result
    }, [])

    return startingHours.length ? Math.min(...startingHours) : null
  }

  const dateRefs = React.useRef<ResultRefs>({})
  const firstShiftIndex = getFirstShiftIndex()

  useEffect(() => {
    const item = dateRefs.current[0]
    if (!item || !view.isDaily) return

    // The first column we want to see after the employees one (by default, 6h)
    // ?? to consider shifts starting at 0
    const scrollTo = firstShiftIndex ?? 6
    const employeesWidth = 240 // px
    const columnMinWidth = item.offsetWidth

    // The columns behind the employees column
    const hiddenColumns = Math.ceil(employeesWidth / columnMinWidth)

    // Do scroll to the relative column to see the selected column after the employees one
    const index = scrollTo - hiddenColumns
    dateRefs.current[index]?.scrollIntoView({ block: 'end', inline: 'start' })

    // Disabling the exhaustive deps rule because we want to run this hook only once instead of re-running it when firstShiftIndex changes
  }, [view.isDaily]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {columns.map((heading: Column, index: number) => (
        <HeaderCell
          key={index}
          heading={heading}
          ref={(node: HTMLDivElement) => (dateRefs.current[heading.hour] = node)}
          index={index}
          onSelect={onSelect}
          checked={checked}
          view={view}
          canManageShifts={canManageShifts}
        />
      ))}
    </>
  )
}

export default observer(Header)
